import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import Cookies from "js-cookie";
import { logoutUser } from "@/services/user";

export type UserType = "ADMIN" | "BROKER";

export interface AuthUser {
    _id: string;
    name: string;
    email: string;
    userType: UserType;
}

interface AuthContextType {
    authUser: AuthUser | null;
    authToken: string | null;
    login: (token: string, user: AuthUser) => void;
    logout: () => void;
    loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [authUser, setAuthUser] = useState<AuthUser | null>(null);
    const [authToken, setAuthToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    // Load from cookies on first mount
    useEffect(() => {
        const token = Cookies.get("authToken");
        const userStr = Cookies.get("authUser");

        if (token && userStr) {
            try {
                setAuthToken(token);
                // Try parsing directly, then try decoding if it fails
                try {
                    setAuthUser(JSON.parse(userStr));
                } catch {
                    setAuthUser(JSON.parse(decodeURIComponent(userStr)));
                }
            } catch (error) {
                console.error("AuthContext: Failed to parse user data", error);
                setAuthToken(null);
                setAuthUser(null);
            }
        }
        setLoading(false);
    }, []);

    const login = (token: string, user: AuthUser) => {
        Cookies.set("authUser", JSON.stringify(user), { expires: 1 });
        Cookies.set("authToken", token, { expires: 1 });
        setAuthToken(token);
        setAuthUser(user);
    };

    const logout = async () => {
        try {
            await logoutUser(); //  call backend logout
        } finally {
            Cookies.remove("authUser");
            Cookies.remove("authToken");
            setAuthUser(null);
            setAuthToken(null);
        }
    };


    return (
        <AuthContext.Provider value={{ authUser, authToken, login, logout, loading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
};
